import type {
  HistoryModeSelectedEvent,
  HistoryToolbarEvent,
} from "@/features/history/historyEvents";
import type { HistoryMode } from "@/features/history/historyMode";
import {
  createMediator,
  type EventDispatchResult,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface HistoryPageState {
  readonly mode: HistoryMode;
  readonly isDesktop: boolean;
  readonly isSettingsOpen: boolean;
}

export type HistoryPageEvent =
  | HistoryModeSelectedEvent
  | HistoryToolbarEvent
  | { readonly type: "history/route-synchronized"; readonly mode: HistoryMode }
  | { readonly type: "history/settings-close-requested" }
  | {
      readonly type: "history/viewport-synchronized";
      readonly isDesktop: boolean;
    };

export type HistoryPageEffect =
  | { readonly type: "navigate-mode"; readonly mode: HistoryMode }
  | { readonly type: "refresh" }
  | { readonly type: "toggle-sidebar" }
  | { readonly type: "scroll"; readonly edge: "top" | "bottom" };

export type HistoryPageEventNotifier = (
  event: HistoryPageEvent,
) => EventDispatchResult<TypedEvent>["status"];

export interface HistoryPageMediatorOptions {
  readonly initialMode: HistoryMode;
  readonly isDesktop: boolean;
  readonly runEffect?: (effect: HistoryPageEffect) => void;
}

export function isHistoryPageEvent(
  event: TypedEvent,
): event is HistoryPageEvent {
  switch (event.type) {
    case "history/mode-selected":
    case "history/route-synchronized":
      return isHistoryModeField(event);
    case "history/refresh-requested":
    case "history/sidebar-toggle-requested":
    case "history/settings-close-requested":
      return true;
    case "history/scroll-requested":
      return isScrollEdge((event as Record<string, unknown>).edge);
    case "history/settings-link-clicked":
      return hasBooleanField(event, "hasModifier");
    case "history/viewport-synchronized":
      return hasBooleanField(event, "isDesktop");
    default:
      return false;
  }
}

export function transitionHistoryPage(
  state: HistoryPageState,
  event: HistoryPageEvent,
): MediatorTransitionResult<HistoryPageState, HistoryPageEffect> {
  switch (event.type) {
    case "history/mode-selected":
      return {
        status: "handled",
        state: { ...state, mode: event.mode },
        effects: [{ type: "navigate-mode", mode: event.mode }],
      };
    case "history/route-synchronized":
      return {
        status: "handled",
        state: { ...state, mode: event.mode },
      };
    case "history/refresh-requested":
      return { status: "handled", state, effects: [{ type: "refresh" }] };
    case "history/sidebar-toggle-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "toggle-sidebar" }],
      };
    case "history/scroll-requested":
      return {
        status: "handled",
        state,
        effects: [{ type: "scroll", edge: event.edge }],
      };
    case "history/settings-link-clicked":
      if (event.hasModifier) return { status: "unhandled" };
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: true },
      };
    case "history/settings-close-requested":
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: false },
      };
    case "history/viewport-synchronized":
      return {
        status: "handled",
        state: {
          ...state,
          isDesktop: event.isDesktop,
          isSettingsOpen: event.isDesktop ? state.isSettingsOpen : false,
        },
      };
  }
}

export function createHistoryPageMediator(
  options: HistoryPageMediatorOptions,
): Mediator<HistoryPageState, HistoryPageEvent, HistoryPageEffect> {
  return createMediator<HistoryPageState, HistoryPageEvent, HistoryPageEffect>(
    {
      mode: options.initialMode,
      isDesktop: options.isDesktop,
      isSettingsOpen: false,
    },
    transitionHistoryPage,
    { runEffect: options.runEffect },
  );
}

function isHistoryModeField(
  event: TypedEvent,
): event is TypedEvent & { readonly mode: HistoryMode } {
  const mode = (event as Record<string, unknown>).mode;
  return mode === "viewed" || mode === "posted";
}

function hasBooleanField(event: TypedEvent, field: string): boolean {
  return typeof (event as Record<string, unknown>)[field] === "boolean";
}

function isScrollEdge(value: unknown): value is "top" | "bottom" {
  return value === "top" || value === "bottom";
}
